import React, {useState, useContext, useEffect} from "react";
import {get, filter} from "lodash";
import {MultiSelectAutocomplete} from "../../../Components/Form/AutoComplete/AutoComplete";
import './SignDocFilter.scss'
import {I18nReactContext} from "../../../contexts/I18nContext";

/**
 * SignDocFilter Component
 *
 * Provides a multi-select autocomplete for filtering by SignDoc (tasks/courses/documents).
 * Automatically resets selection when the available options change
 * and the currently selected values no longer exist in the new options.
 *
 * @param {object} filterModel - The filter model containing filterData and filterValues
 * @param {function} setFilterModel - Function to update the filter model
 * @param {object} disabledObj - Object containing disabled state and disabled note
 */
export const SignDocFilter = ({filterModel, setFilterModel, disabledObj}) => {
    const {i18n} = useContext(I18nReactContext);

    const [selectedSignDocs, setSelectedSignDocs] = useState(() => {
        const signDocIds = get(filterModel, 'filterValues.signDocIds', ['all']);
        if (signDocIds.includes('all')) return [];
        const signDocsData = get(filterModel, 'filterData.signDocs', []);
        return signDocsData.filter(sd => signDocIds.includes(sd.value));
    });

    /**
     * Syncs local state with filterModel when signDoc options change.
     * Keeps only selections that still exist in the new options, resets to 'all' if none remain.
     */
    useEffect(() => {
        const signDocOptions = get(filterModel, 'filterData.signDocs', []);
        const currentIds = get(filterModel, 'filterValues.signDocIds', ['all']);

        if (currentIds.includes('all')) {
            setSelectedSignDocs([]);
            return;
        }

        const validOptions = signDocOptions.filter(opt => currentIds.includes(opt.value) && opt.value !== 'all');

        if (validOptions.length === 0) {
            setSelectedSignDocs([]);
            setFilterModel(prev => ({
                ...prev,
                filterValues: {
                    ...prev.filterValues,
                    signDocIds: ['all']
                }
            }));
        } else {
            setSelectedSignDocs(validOptions);
        }
    }, [filterModel.filterData.signDocs]);

    /**
     * Handles multi-select signDoc change.
     * When "all" is selected or nothing is selected, sends ['all'] to indicate no filtering.
     *
     * @param {Array} selectedDocs - Array of selected signDoc objects with {value, text}
     */
    const onSelectSignDocsChange = (selectedDocs) => {
        const hasAllOption = selectedDocs?.find(sd => sd.value === 'all');
        if (!selectedDocs || selectedDocs.length === 0 || hasAllOption) {
            setSelectedSignDocs([]);
            setFilterModel(prev => ({
                ...prev,
                filterValues: {
                    ...prev.filterValues,
                    signDocIds: ['all']
                }
            }));
            return;
        }

        setSelectedSignDocs(selectedDocs);
        setFilterModel(prev => ({
            ...prev,
            filterValues: {
                ...prev.filterValues,
                signDocIds: selectedDocs.map(sd => sd.value)
            }
        }));
    };

    /**
     * Filters signDoc options based on search query for the autocomplete dropdown.
     *
     * @param {object} params - Function parameters
     * @param {string} params.query - Search query string
     * @returns {Array} Filtered signDoc options
     */
    const getFilteredSignDocs = ({query}) => {
        const signDocs = get(filterModel, 'filterData.signDocs', []);

        if (!query || query.trim().length === 0) {
            return signDocs;
        }

        return filter(signDocs, sd =>
            sd.text.toLowerCase().includes(query.toLowerCase())
        );
    };

    const signDocs = get(filterModel, 'filterData.signDocs', []);

    return (
        <div className="SignDocFilter">
            <MultiSelectAutocomplete
                selectAllText={i18n.translate('allSignDocs')}
                enableSelectAll={true}
                label={String(i18n.translate('dataGrid.filterLabels.signDoc'))}
                fetchOptions={getFilteredSignDocs}
                initialSelectedValues={selectedSignDocs}
                onSelectionChange={onSelectSignDocsChange}
                refreshTrigger={signDocs.length}
                disabled={get(disabledObj, 'disabled')}
            />
            {get(disabledObj, 'disabled') ? <div className="disabledNotice">{get(disabledObj, 'disabledNote')}</div> : ''}
        </div>
    );
}
